import { FC, SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react'

import { LoadingSpinner, Minus, Plus, ScreenReaderOnly } from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'

import { Button, Container, Input, SpinnerWrapper } from './styles'

const ANNOUNCEMENT_DELAY_MS = 150

/**
 * Manages `inputQuantity` state that also updates when a new `originalQuantity` is passed as a prop. The default `React.useState` behavior only uses the initial state on FIRST render.
 */
function usePropReactiveInputQuantity(originalQuantity: string) {
  const [inputQuantity, setInputQuantity] = useState<string | undefined>(originalQuantity)

  useEffect(() => {
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
    setInputQuantity(originalQuantity)
  }, [originalQuantity])

  return {
    inputQuantity,
    setInputQuantity,
  }
}

interface CartItemQuantityProps {
  cartIsUpdating: boolean
  decrementQuantity(): void
  incrementQuantity(): void
  setQuantity(newQuantity: number): void
  quantity: number
}

export const CartItemQuantity: FC<CartItemQuantityProps> = ({
  cartIsUpdating,
  decrementQuantity,
  incrementQuantity,
  setQuantity,
  quantity,
}) => {
  const originalQuantity: string = quantity.toString()
  const [itemIsUpdating, setItemIsUpdating] = useState<boolean>(false)
  const {
    cart: { item: cartItemStrings },
    loadMoreButton: { loading: loadingLabel },
  } = useTranslation()
  const [quantityAnnouncement, setQuantityAnnouncement] = useState('')
  // Handle for the pending announcement timeout, so a superseding settle (or unmount)
  // can cancel it before it fires.
  const announcementTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const safeQuantityUpdate = useCallback(
    (updateQuantity: () => void) => (_e: SyntheticEvent) => {
      if (cartIsUpdating) return
      setItemIsUpdating(true)
      updateQuantity()
    },
    [cartIsUpdating]
  )

  // When a cart update settles (item was updating, cart no longer is), schedule a
  // screen-reader announcement of the new quantity. The delay lets the browser's
  // focus announcement finish first. clearTimeout collapses a rapid burst of settles into a single announcement.
  useEffect(() => {
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (itemIsUpdating && !cartIsUpdating) {
      // TODO: Fix this auto-generated suppression.
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates, react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setItemIsUpdating(false)
      const message = cartItemStrings.quantityUpdated.replace('{{quantity}}', originalQuantity)
      clearTimeout(announcementTimer.current)
      announcementTimer.current = setTimeout(() => {
        setQuantityAnnouncement(message)
      }, ANNOUNCEMENT_DELAY_MS)
    }
  }, [itemIsUpdating, cartIsUpdating, cartItemStrings.quantityUpdated, originalQuantity])

  // Cancel any pending announcement only when the component actually unmounts
  useEffect(() => () => clearTimeout(announcementTimer.current), [])

  const { inputQuantity, setInputQuantity } = usePropReactiveInputQuantity(originalQuantity)

  function setQuantityIfValid(): void {
    if (inputQuantity === originalQuantity) return
    if (inputQuantity === undefined || !inputQuantity.trim())
      return setInputQuantity(originalQuantity)
    const newQuantity = parseInt(inputQuantity, 10)
    if (isNaN(newQuantity)) return setInputQuantity(originalQuantity)
    setQuantity(newQuantity)
  }

  return (
    <Container>
      <ScreenReaderOnly role='status' aria-live='polite' aria-atomic='true'>
        {quantityAnnouncement}
      </ScreenReaderOnly>
      <Button
        aria-label={cartItemStrings.decrementQuantity}
        aria-disabled={cartIsUpdating}
        onClick={safeQuantityUpdate(decrementQuantity)}
      >
        <Minus stroke='currentColor' />
      </Button>
      {itemIsUpdating ? (
        <SpinnerWrapper>
          <LoadingSpinner label={loadingLabel} />
        </SpinnerWrapper>
      ) : (
        <Input
          onChange={event => setInputQuantity(event.target.value)}
          onBlur={setQuantityIfValid}
          type='number'
          value={inputQuantity}
        />
      )}
      <Button
        aria-label={cartItemStrings.incrementQuantity}
        aria-disabled={cartIsUpdating}
        onClick={safeQuantityUpdate(incrementQuantity)}
      >
        <Plus stroke='currentColor' />
      </Button>
    </Container>
  )
}
